import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import { CampoUtenteDef } from '../../_models/CampoUtenteDef';
import { EntitaCampoUtente } from '../../_models/generated/entita-campo-utente';
import {CampiUtenteService} from '../../_services/campi-utente.service';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {SaveMessage} from '../../_helpers/save-message';
import {CampiUtenteFormComponent} from '../campi-utente-form/campi-utente-form.component';

/**
 * Campi utente della membership operatore↔organizzazione (feature 136 / issue #3528).
 *
 * Un solo componente per i due punti di compilazione: la sezione del modale "Opzioni operatore"
 * (l'operatore sui propri campi, `showSaveButton = false`: salva il modale) e il tab del dettaglio
 * operatore (il MANAGER sui campi altrui, `showSaveButton = true`: FAB proprio del tab).
 */
@Component({
  selector: 'app-campi-utente-account',
  templateUrl: './campi-utente-account.component.html',
  styleUrls: ['./campi-utente-account.component.scss'],
  standalone: false,
})
export class CampiUtenteAccountComponent implements OnInit {
  @Input() orgId: number;
  @Input() accountId: number;
  /**
   * Valori digitati e non ancora salvati, consolidati dal parent prima che la view venga distrutta
   * (cambio tab). Quando è definito VINCE sul valore letto dal server (FR-016a): il server è il
   * fallback della prima apertura.
   */
  @Input() valoriPending?: string;
  /** true nel tab del dettaglio operatore: il salvataggio è del componente (FAB proprio). */
  @Input() showSaveButton = false;

  /**
   * Emesso a caricamento concluso: `false` quando l'organizzazione non ha definizioni operatore.
   * Serve al modale "Opzioni operatore", dove in quel caso la sezione non deve proprio comparire
   * (US2 §3); il tab del dettaglio operatore mostra invece lo stato vuoto esplicito (FR-014).
   */
  @Output() definizioniDisponibili = new EventEmitter<boolean>();

  /** Emesso a salvataggio riuscito: il tab del dettaglio operatore spegne l'indicatore di modifiche. */
  @Output() salvato = new EventEmitter<void>();

  @ViewChild(CampiUtenteFormComponent) form?: CampiUtenteFormComponent;

  definizioniAttive: CampoUtenteDef[] = [];
  definizioniStoriche: CampoUtenteDef[] = [];
  valore: string | null = null;
  /**
   * Valore a database, letto all'apertura e riaggiornato a ogni salvataggio riuscito. È il termine
   * di paragone di `isDirty()`: NON coincide con `valore`, che può portare il buffer dei valori
   * digitati e non salvati (`valoriPending`).
   */
  private valoreServer: string | null = null;
  loading = false;
  saving = false;

  constructor(
    private campiUtenteService: CampiUtenteService,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) { }

  ngOnInit() {
    this.load();
  }

  get hasDefinizioni(): boolean {
    return this.definizioniAttive.length > 0 || this.definizioniStoriche.length > 0;
  }

  /** Valori correnti del form, o il buffer se la view non è (più) montata. */
  serialize(): string | null {
    return this.form ? this.form.serialize() : this.valoriPending ?? null;
  }

  /**
   * true quando i valori correnti differiscono da quelli a database.
   *
   * Il confronto è semantico e ristretto ai codici delle definizioni ATTIVE: `serialize()` omette
   * i valori storici e quelli vuoti, quindi un confronto fra stringhe grezze segnalerebbe come
   * "modificato" un form che nessuno ha toccato — bastava che il record portasse un valore storico
   * o che le chiavi fossero in ordine diverso.
   */
  isDirty(): boolean {
    const codici = this.definizioniAttive.map(d => d.codice);
    return CampiUtenteAccountComponent.perConfronto(this.serialize(), codici)
      !== CampiUtenteAccountComponent.perConfronto(this.valoreServer, codici);
  }

  /** Proiezione canonica di un JSON di valori sui soli codici dati, in ordine stabile. */
  private static perConfronto(json: string | null, codici: string[]): string {
    let obj: Record<string, unknown> = {};
    try {
      obj = json ? JSON.parse(json) as Record<string, unknown> : {};
    } catch {
      return json ?? '';
    }
    const out: Record<string, string> = {};
    for (const codice of [...codici].sort()) {
      const v = obj?.[codice];
      if (v !== undefined && v !== null && String(v) !== '') out[codice] = String(v);
    }
    return JSON.stringify(out);
  }

  async save(): Promise<boolean> {
    if (!this.hasDefinizioni) return true;
    this.saving = true;
    try {
      await this.organizzazioniService
        .updateAccountOrganizzazioneCampiUtenteAsync(this.orgId, this.accountId, this.serialize() ?? '{}');
      // ⛔ Nessun setCurrentAccountSettingsValue() qui: ripopolare la cache condivisa dal percorso
      // dedicato reintrodurrebbe il valore stale che l'ignore incondizionato elimina (FR-013).
      this.valoreServer = this.serialize();
      this.saveMessage.messageInfo('Campi utente salvati.');
      this.salvato.emit();
      return true;
    } catch (err) {
      this.saveMessage.messageError(this.messaggioErrore(err));
      return false;
    } finally {
      this.saving = false;
    }
  }

  /** Messaggio del 400 di validazione del backend, con fallback leggibile. */
  messaggioErrore(err: unknown): string {
    return (err as { error?: { errorMessage?: string } })?.error?.errorMessage
      ?? 'Ci sono problemi con il salvataggio dei campi utente.';
  }

  private load() {
    if (!this.orgId || !this.accountId) return;
    this.loading = true;
    // soloAttive = false: senza le storiche il form non può rendere in sola lettura i valori
    // delle definizioni disattivate (FR-015).
    const definizioni = this.campiUtenteService
      .getDefinizioni(this.orgId, false, EntitaCampoUtente.ACCOUNT_ORGANIZZAZIONE);
    // I valori si leggono SEMPRE dal server, mai dalla cache localStorage delle impostazioni
    // operatore: da lì sarebbero stale, o assenti per una cache scritta prima del rilascio (FR-012).
    const account = this.organizzazioniService.getAccountOrganizzazioneAsync(this.orgId, this.accountId);

    Promise.all([definizioni, account])
      .then(([defs, acc]) => {
        this.definizioniAttive = (defs ?? []).filter(d => d.isActive);
        this.definizioniStoriche = (defs ?? []).filter(d => !d.isActive);
        // Precedenza del buffer (FR-016a), in un punto solo: il fetch arriva dopo il binding
        // dell'input e senza questa regola sovrascriverebbe i valori digitati.
        this.valoreServer = acc?.campiUtente ?? null;
        this.valore = this.valoriPending ?? this.valoreServer;
        this.definizioniDisponibili.emit(this.hasDefinizioni);
      })
      .catch(() => this.saveMessage.messageError('Ci sono problemi con il caricamento dei campi utente.'))
      .finally(() => this.loading = false);
  }
}
